import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ChevronLeft, Flame, Save, Upload, Trash2, FileText, Loader2, ClipboardList, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

const TODAY_ISO = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

const formatMMDDYYYY = (value) => {
  if (!value) return '—';
  const dateOnly = String(value).split('T')[0];
  const m = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return dateOnly;
  return `${m[2]}-${m[3]}-${m[1]}`;
};

const blankFormFor = (row) => ({
  filler_metal_type: row.filler_metal_type || '',
  material_group: row.material_group || '',
  positions: row.positions || '',
  additional_notes: row.additional_notes || '',
  certification_date: row.certification_date ? String(row.certification_date).split('T')[0] : '',
});

// 2026-07-17 — MaintenanceHeader (top-level Initial Certification Date +
// legacy Due Date / Days Remaining / Status) removed entirely. The Active
// Qualifications table below is now the sole source of truth for
// per-process seed cert dates.

function QualificationTableRow({ row, api, technicianId, onSaved }) {
  const [form, setForm] = useState(blankFormFor(row));
  const [pristine, setPristine] = useState(blankFormFor(row));
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const fresh = blankFormFor(row);
    setForm(fresh);
    setPristine(fresh);
    // If the parent re-fetches mid-edit, drop out of edit mode to avoid stale state.
    setEditing(false);
  }, [row]);

  const isDirty =
    form.filler_metal_type !== pristine.filler_metal_type ||
    form.material_group !== pristine.material_group ||
    form.positions !== pristine.positions ||
    form.additional_notes !== pristine.additional_notes ||
    form.certification_date !== pristine.certification_date;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(
        `/lms/welding-personnel/${technicianId}/qualification-details/${row.id}`,
        {
          filler_metal_type: form.filler_metal_type || null,
          material_group: form.material_group || null,
          positions: form.positions || null,
          additional_notes: form.additional_notes || null,
          certification_date: form.certification_date || null,
        }
      );
      toast.success('Qualification updated');
      setEditing(false);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(pristine);
    setEditing(false);
  };

  // ---- Read-only mode: render plain values, with an Edit action ----
  if (!editing) {
    const display = (v) => (v ? v : <span className="text-gray-400">—</span>);
    return (
      <tr
        className="border-b last:border-b-0 hover:bg-gray-50/50"
        data-testid={`welder-qual-row-${row.id}`}
      >
        <td className="px-3 py-3 font-medium whitespace-nowrap">{row.process}</td>
        <td className="px-3 py-3 whitespace-nowrap text-gray-600">{row.standard_label}</td>
        <td className="px-3 py-3 text-sm">{display(pristine.filler_metal_type)}</td>
        <td className="px-3 py-3 text-sm">{display(pristine.material_group)}</td>
        <td className="px-3 py-3 text-sm">{display(pristine.positions)}</td>
        <td className="px-3 py-3 text-sm whitespace-nowrap">
          {pristine.certification_date
            ? formatMMDDYYYY(pristine.certification_date)
            : <span className="text-gray-400">—</span>}
        </td>
        <td className="px-3 py-3 text-sm">
          {pristine.additional_notes
            ? <span className="text-gray-600 whitespace-pre-wrap break-words">{pristine.additional_notes}</span>
            : <span className="text-gray-400">—</span>}
        </td>
        <td className="px-3 py-3 text-right">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            data-testid={`welder-qual-edit-${row.id}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        </td>
      </tr>
    );
  }

  // ---- Edit mode: editable cells + Save / Cancel ----
  return (
    <tr
      className="border-b last:border-b-0 bg-blue-50/30"
      data-testid={`welder-qual-row-${row.id}`}
    >
      <td className="px-3 py-2 font-medium whitespace-nowrap">{row.process}</td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{row.standard_label}</td>
      <td className="px-3 py-2">
        <Input
          value={form.filler_metal_type}
          onChange={(e) => setForm({ ...form, filler_metal_type: e.target.value })}
          maxLength={200}
          className="h-8 text-sm"
          data-testid={`welder-filler-${row.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={form.material_group}
          onChange={(e) => setForm({ ...form, material_group: e.target.value })}
          maxLength={200}
          className="h-8 text-sm"
          data-testid={`welder-material-${row.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={form.positions}
          onChange={(e) => setForm({ ...form, positions: e.target.value })}
          maxLength={200}
          placeholder="1G, 2G, 3G"
          className="h-8 text-sm"
          data-testid={`welder-positions-${row.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="date"
          value={form.certification_date}
          max={TODAY_ISO}
          onChange={(e) => setForm({ ...form, certification_date: e.target.value })}
          className="h-8 text-sm"
          data-testid={`welder-certdate-${row.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Textarea
          value={form.additional_notes}
          onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
          maxLength={2000}
          rows={1}
          className="text-sm min-h-[2rem]"
          data-testid={`welder-notes-${row.id}`}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={saving}
            data-testid={`welder-qual-cancel-${row.id}`}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || saving}
            data-testid={`welder-qual-save-${row.id}`}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function QualificationMobileCard({ row, api, technicianId, onSaved }) {
  const [form, setForm] = useState(blankFormFor(row));
  const [pristine, setPristine] = useState(blankFormFor(row));
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const fresh = blankFormFor(row);
    setForm(fresh);
    setPristine(fresh);
    setEditing(false);
  }, [row]);

  const isDirty =
    form.filler_metal_type !== pristine.filler_metal_type ||
    form.material_group !== pristine.material_group ||
    form.positions !== pristine.positions ||
    form.additional_notes !== pristine.additional_notes ||
    form.certification_date !== pristine.certification_date;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(
        `/lms/welding-personnel/${technicianId}/qualification-details/${row.id}`,
        {
          filler_metal_type: form.filler_metal_type || null,
          material_group: form.material_group || null,
          positions: form.positions || null,
          additional_notes: form.additional_notes || null,
          certification_date: form.certification_date || null,
        }
      );
      toast.success('Qualification updated');
      setEditing(false);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(pristine);
    setEditing(false);
  };

  if (!editing) {
    const display = (v) => v || <span className="text-gray-400">—</span>;
    return (
      <Card data-testid={`welder-qual-mobile-${row.id}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>
              {row.process}{' '}
              <span className="text-xs text-gray-500 font-normal">· {row.standard_label}</span>
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
              data-testid={`welder-qual-edit-mobile-${row.id}`}
            >
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div><span className="text-xs text-gray-500">Filler Metal Type:</span> {display(pristine.filler_metal_type)}</div>
          <div><span className="text-xs text-gray-500">Material Group:</span> {display(pristine.material_group)}</div>
          <div><span className="text-xs text-gray-500">Positions:</span> {display(pristine.positions)}</div>
          <div><span className="text-xs text-gray-500">Certification Date:</span> {pristine.certification_date ? formatMMDDYYYY(pristine.certification_date) : <span className="text-gray-400">—</span>}</div>
          <div className="whitespace-pre-wrap"><span className="text-xs text-gray-500">Additional Notes:</span> {display(pristine.additional_notes)}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid={`welder-qual-mobile-${row.id}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>
            {row.process}{' '}
            <span className="text-xs text-gray-500 font-normal">· {row.standard_label}</span>
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || saving}
              data-testid={`welder-qual-save-mobile-${row.id}`}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Filler Metal Type</Label>
          <Input value={form.filler_metal_type} maxLength={200}
            onChange={(e) => setForm({ ...form, filler_metal_type: e.target.value })}
            className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Material Group</Label>
          <Input value={form.material_group} maxLength={200}
            onChange={(e) => setForm({ ...form, material_group: e.target.value })}
            className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Positions</Label>
          <Input value={form.positions} maxLength={200} placeholder="1G, 2G, 3G"
            onChange={(e) => setForm({ ...form, positions: e.target.value })}
            className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Certification Date</Label>
          <Input type="date" value={form.certification_date} max={TODAY_ISO}
            onChange={(e) => setForm({ ...form, certification_date: e.target.value })}
            className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Additional Notes</Label>
          <Textarea value={form.additional_notes} maxLength={2000} rows={2}
            onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
            className="mt-1" />
        </div>
      </CardContent>
    </Card>
  );
}

function FilesSection({ technicianId, api, user }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [fileObj, setFileObj] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canDelete = ['ADMIN', 'MANAGER', 'OPERATOR'].includes(user?.role);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lms/welding-personnel/${technicianId}/files`);
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [api, technicianId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async () => {
    if (!fileObj || !title.trim()) {
      toast.error('Title and PDF file are required');
      return;
    }
    if (fileObj.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (fileObj.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('file', fileObj);
      await api.post(`/lms/welding-personnel/${technicianId}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setTitle('');
      setFileObj(null);
      const input = document.getElementById(`welder-file-input-${technicianId}`);
      if (input) input.value = '';
      fetchFiles();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/lms/welding-personnel/files/${deleteTarget.id}`);
      toast.success('Document deleted');
      setDeleteTarget(null);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> Records & Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload row — balanced three-column grid with the button anchored to
            the input baseline. Helper text sits below the file picker without
            shifting any field heights. */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 md:gap-5">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. AAR SMAW Cert 2026"
              className="mt-1.5 h-9"
              data-testid="welder-doc-title"
            />
          </div>
          <div>
            <Label className="text-xs">PDF File</Label>
            <Input
              id={`welder-file-input-${technicianId}`}
              type="file"
              accept="application/pdf"
              onChange={(e) => setFileObj(e.target.files?.[0] || null)}
              className="mt-1.5 h-9 file:mr-3 file:text-xs file:rounded file:border-0 file:bg-gray-100 file:px-2 file:py-1"
              data-testid="welder-doc-file"
            />
          </div>
          <div className="flex md:justify-end md:items-end">
            <Button
              onClick={handleUpload}
              disabled={uploading || !fileObj || !title.trim()}
              className="h-9 md:mt-[22px] w-full md:w-auto md:min-w-[140px]"
              data-testid="welder-doc-upload-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Upload</>}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 -mt-3">PDF only, max 10 MB.</p>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : files.length === 0 ? (
          <div className="border-t pt-4">
            <p className="text-sm text-gray-500" data-testid="welder-docs-empty">
              No documents uploaded yet.
            </p>
          </div>
        ) : (
          <div className="border-t pt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-500">
                <tr className="text-left">
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Filename</th>
                  <th className="px-3 py-2">Uploaded By</th>
                  <th className="px-3 py-2">Uploaded At</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {files.map((f) => (
                  <tr key={f.id} data-testid={`welder-doc-row-${f.id}`}>
                    <td className="px-3 py-2.5 font-medium">{f.title}</td>
                    <td className="px-3 py-2.5 text-gray-600">{f.filename}</td>
                    <td className="px-3 py-2.5 text-gray-600">{f.uploaded_by_name || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <a
                          href={f.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-xs"
                          data-testid={`welder-doc-view-${f.id}`}
                        >
                          View
                        </a>
                        <a
                          href={f.file_url}
                          download={f.filename}
                          className="text-blue-600 hover:underline text-xs"
                          data-testid={`welder-doc-download-${f.id}`}
                        >
                          Download
                        </a>
                        {canDelete && (
                          <button
                            onClick={() => setDeleteTarget(f)}
                            className="text-red-600 hover:underline text-xs inline-flex items-center gap-1"
                            data-testid={`welder-doc-delete-${f.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="welder-doc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hide "{deleteTarget?.title}" from the active list. The file is preserved on disk for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function WeldingPersonnelDetailPage() {
  const { technician_id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isTech = user?.role === 'TECHNICIAN';

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/lms/welding-personnel/${technician_id}`);
      setData(payload);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load welder');
    } finally {
      setLoading(false);
    }
  }, [api, technician_id]);

  useEffect(() => { if (!isTech) fetchDetail(); }, [fetchDetail, isTech]);

  if (isTech) {
    return (
      <div className="p-8" data-testid="welder-detail-forbidden">
        <h1 className="text-2xl font-semibold text-gray-800">Access Denied</h1>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-12 flex justify-center" data-testid="welder-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="welding-personnel-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/training/welding-personnel')}
            data-testid="welder-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
            <Flame className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{data.technician_name}</h1>
            <p className="text-xs text-gray-500">
              {data.technician_employee_id ? `${data.technician_employee_id} · ` : ''}
              {data.facility_name || '—'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/training/welder/${technician_id}`)}
          data-testid="welder-view-log-btn"
        >
          <ClipboardList className="h-4 w-4 mr-2" /> View Maintenance Log
        </Button>
      </div>

      {/* 2026-07-17 — Top-level Initial Certification Date section removed
          entirely. Active Qualifications now sit directly under the header
          and are the sole source of truth for per-process certification
          seed dates. */}

      {/* Per-row qualification details */}
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-[#323033]">Active Qualifications</h2>
        <p className="text-xs text-gray-500">
          One row per active (Process × Standard) combination from User Management.
        </p>
      </div>
      {data.qualification_rows.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-gray-500" data-testid="welder-no-rows">
            No active welding qualifications. Re-check a qualification in User Management to restore.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop: true table layout */}
          <Card className="hidden md:block">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="welder-quals-table">
                <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-500">
                  <tr className="text-left">
                    <th className="px-3 py-2">Process</th>
                    <th className="px-3 py-2">Standard</th>
                    <th className="px-3 py-2">Filler Metal Type</th>
                    <th className="px-3 py-2">Material Group</th>
                    <th className="px-3 py-2">Positions</th>
                    <th className="px-3 py-2">Certification Date</th>
                    <th className="px-3 py-2">Additional Notes</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.qualification_rows.map((row) => (
                    <QualificationTableRow
                      key={row.id}
                      row={row}
                      api={api}
                      technicianId={technician_id}
                      onSaved={fetchDetail}
                    />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          {/* Mobile fallback: stacked cards */}
          <div className="space-y-4 md:hidden">
            {data.qualification_rows.map((row) => (
              <QualificationMobileCard
                key={row.id}
                row={row}
                api={api}
                technicianId={technician_id}
                onSaved={fetchDetail}
              />
            ))}
          </div>
        </>
      )}

      {/* Documents section — files persist independent of qualification rows */}
      <FilesSection technicianId={technician_id} api={api} user={user} />
    </div>
  );
}
